import { ApiError } from '@ignite/api-client';
import {
  AI_CHAT_ATTACHMENT_MAX_COUNT,
  type AiChatCompletionRequest,
  type AiChatCompletionResponse,
  type AiChatConversation,
  type AiChatStoredMessage,
  type AiChatStreamEvent,
  type UserAiFeatureModels,
  type UserAiModelOption,
} from '@ignite/types';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiClient, readStoredSessionIdentity, readStoredUser } from '../../../lib/auth';
import {
  chatAttachmentCreateRequest,
  ChatFileMetadataError,
  createAttachmentLocalId,
} from './chat-files';
import {
  apiErrorInfo,
  buildChatRequestMessages,
  CHAT_GENERATION_MAX_FOLLOW_MS,
  CHAT_MAX_OUTPUT_TOKENS,
  CHAT_MESSAGE_MAX_COUNT,
  CHAT_STREAM_MAX_RETRIES,
  chatAttachmentIssue,
  chatComposerKey,
  chatStreamErrorDisposition,
  chatStreamRetryDelayMs,
  clearPersistedChatState,
  createClientMessageId,
  createEmptyChatState,
  createEmptyComposer,
  findChatModel,
  isDeterministicAdmissionError,
  persistChatState,
  readPersistedChatState,
  resolveChatModelId,
  selectChatRequestHistory,
  type ChatAttachmentIssue,
  type ChatAttachmentItem,
  type ChatComposerState,
  type ChatPersistedState,
  type ChatRun,
} from './chat-state';

const CHAT_EXPIRED_RESULT_READ_GRACE_MS = 10_000;
const CHAT_MESSAGES_PAGE_LIMIT = 50;

export type ChatDisplayMessage = {
  id: string;
  conversationId: string | null;
  role: 'user' | 'assistant';
  content: string | null;
  attachments: AiChatStoredMessage['attachments'];
  status: AiChatStoredMessage['status'];
  requestedModelId: string | null;
  modelId: string | null;
  usage: AiChatStoredMessage['usage'];
  error: AiChatStoredMessage['error'];
  response: AiChatCompletionResponse | null;
  startedAt: number | null;
  completedAt: number | null;
  local: boolean;
};

type CurrentRunTiming = {
  clientMessageId: string;
  startedAt: number;
  completedAt: number | null;
};

type ChatRunFence = {
  clientMessageId: string;
  generationId: string | null;
};

function createChatRunFence(run: ChatRun): ChatRunFence {
  return {
    clientMessageId: run.request.client_message_id,
    generationId: run.generationId,
  };
}

function chatRunFenceMatches(run: ChatRun | null, fence: ChatRunFence): boolean {
  return Boolean(
    run &&
    run.request.client_message_id === fence.clientMessageId &&
    (fence.generationId === null || run.generationId === fence.generationId),
  );
}

type ChatContextValue = {
  open: boolean;
  historyOpen: boolean;
  selectedConversationId: string | null;
  selectedConversation: AiChatConversation | null;
  conversations: AiChatConversation[];
  conversationsLoading: boolean;
  conversationsError: unknown;
  conversationsHasMore: boolean;
  conversationsFetchingMore: boolean;
  messages: ChatDisplayMessage[];
  historyMessages: AiChatStoredMessage[];
  messagesLoading: boolean;
  messagesError: unknown;
  messagesHasMore: boolean;
  messagesFetchingMore: boolean;
  composer: ChatComposerState;
  modelFeature: UserAiFeatureModels | undefined;
  modelOptions: UserAiModelOption[];
  selectedModel: UserAiModelOption | null;
  modelsLoading: boolean;
  modelsError: unknown;
  attachmentIssue: ChatAttachmentIssue | null;
  attachmentMaxCount: number;
  attachmentActionIds: ReadonlySet<string>;
  canSend: boolean;
  busy: boolean;
  activeRun: ChatRun | null;
  unreadCount: number;
  lastOutcome: ChatPersistedState['lastOutcome'];
  focusRequest: number;
  historyMutationId: string | null;
  conversationSelectionError: boolean;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setHistoryOpen: (open: boolean) => void;
  selectConversation: (conversationId: string) => Promise<boolean>;
  startNewConversation: () => void;
  setDraft: (draft: string) => void;
  setModelId: (modelId: string) => void;
  addAttachments: (files: readonly File[]) => Promise<void>;
  retryAttachment: (localId: string) => Promise<void>;
  removeAttachment: (localId: string) => Promise<void>;
  send: () => void;
  resumeActiveRun: () => void;
  prepareRetry: (message: ChatDisplayMessage, previousUser: ChatDisplayMessage | null) => void;
  loadMoreConversations: () => void;
  retryConversations: () => void;
  loadMoreMessages: () => void;
  retryMessages: () => void;
  retryModels: () => void;
  renameConversation: (conversationId: string, title: string) => Promise<boolean>;
  deleteConversation: (conversationId: string) => Promise<boolean>;
  reset: () => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const ownerKey = useMemo(currentChatOwnerKey, []);
  const queryClient = useQueryClient();
  const [state, setState] = useState<ChatPersistedState>(() => readPersistedChatState(ownerKey));
  const [open, setOpenState] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [admissionLoading, setAdmissionLoading] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const [attachmentActionIds, setAttachmentActionIds] = useState<Set<string>>(() => new Set());
  const [historyMutationId, setHistoryMutationId] = useState<string | null>(null);
  const [conversationSelectionError, setConversationSelectionError] = useState(false);
  const stateRef = useRef(state);
  const openRef = useRef(open);
  const streamControllerRef = useRef<AbortController | null>(null);
  const followingRef = useRef(false);
  const admissionRef = useRef(false);
  const recoveryStartedRef = useRef(false);
  const currentRunTimingRef = useRef<CurrentRunTiming | null>(null);
  const conversationSelectionTokenRef = useRef(0);

  const updateState = useCallback((update: (current: ChatPersistedState) => ChatPersistedState) => {
    const next = update(stateRef.current);
    stateRef.current = next;
    setState(next);
  }, []);

  useEffect(() => {
    stateRef.current = state;
    persistChatState(ownerKey, state);
  }, [ownerKey, state]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const modelsQuery = useQuery({
    queryKey: ['chat', ownerKey, 'models'],
    queryFn: () => apiClient.userAiModels(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const modelFeature = modelsQuery.data?.features.find(
    (feature) => feature.feature_key === 'chat_completion',
  );

  const conversationsQuery = useInfiniteQuery({
    queryKey: ['chat', ownerKey, 'conversations'],
    queryFn: ({ pageParam }) =>
      apiClient.listChatConversations({
        ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
        limit: 30,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    retry: false,
  });
  const conversations = useMemo(
    () => conversationsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [conversationsQuery.data],
  );

  const messagesQuery = useInfiniteQuery({
    queryKey: chatMessagesQueryKey(ownerKey, state.selectedConversationId),
    queryFn: ({ pageParam, signal }) => {
      const conversationId = state.selectedConversationId;
      if (!conversationId) throw new Error('chat conversation is not selected');
      return loadChatMessagesPage(conversationId, pageParam, signal);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: state.selectedConversationId !== null,
    retry: false,
  });
  const historyMessages = useMemo(
    () =>
      messagesQuery.data
        ? [...messagesQuery.data.pages]
            .reverse()
            .flatMap((page) => page.items)
            .filter(
              (message, index, items) =>
                items.findIndex((item) => item.id === message.id) === index,
            )
        : [],
    [messagesQuery.data],
  );
  const selectedConversation =
    conversations.find((conversation) => conversation.id === state.selectedConversationId) ??
    messagesQuery.data?.pages[0]?.conversation ??
    null;

  const composerKey = chatComposerKey(state.selectedConversationId);
  const composer = state.composers[composerKey] ?? createEmptyComposer(null);
  const resolvedModelId =
    state.selectedConversationId !== null && !selectedConversation
      ? null
      : resolveChatModelId(modelFeature, {
          conversationDefaultModelId: selectedConversation?.default_model_id,
          composerModelId: composer.modelId,
          recentModelId: state.recentModelId,
        });
  const selectedModel = findChatModel(modelFeature, resolvedModelId);
  const attachmentMaxCount = Math.min(
    AI_CHAT_ATTACHMENT_MAX_COUNT,
    selectedModel?.supports.chat_attachments.max_count ?? 0,
  );
  const attachmentValidation = chatAttachmentIssue(composer.attachments, selectedModel);
  const busy =
    admissionLoading ||
    state.activeRun?.status === 'sending' ||
    state.activeRun?.status === 'processing';
  const draft = composer.draft.trim();
  const contextReady = state.selectedConversationId === null || messagesQuery.isSuccess;
  const uncertainAdmission = Boolean(
    state.activeRun?.status === 'failed' &&
    state.activeRun.recoveryRequired &&
    !state.activeRun.generationId,
  );
  const canSend = Boolean(
    contextReady && draft && selectedModel && !attachmentValidation && !busy && !uncertainAdmission,
  );

  useEffect(() => {
    if (!resolvedModelId || composer.modelId === resolvedModelId) return;
    updateState((current) => {
      const currentKey = chatComposerKey(current.selectedConversationId);
      const currentComposer = current.composers[currentKey] ?? createEmptyComposer(null);
      return {
        ...current,
        recentModelId: current.recentModelId ?? resolvedModelId,
        composers: {
          ...current.composers,
          [currentKey]: { ...currentComposer, modelId: resolvedModelId },
        },
      };
    });
  }, [composer.modelId, resolvedModelId, updateState]);

  const updateActiveRunForFence = useCallback(
    (fence: ChatRunFence, update: Partial<ChatRun>): boolean => {
      let applied = false;
      updateState((current) => {
        if (!chatRunFenceMatches(current.activeRun, fence)) return current;
        applied = true;
        return {
          ...current,
          activeRun: { ...current.activeRun!, ...update },
        };
      });
      return applied;
    },
    [updateState],
  );

  const invalidateConversation = useCallback(
    async (conversationId: string | null) => {
      const requests: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: ['chat', ownerKey, 'conversations'] }),
      ];
      if (conversationId) {
        requests.push(
          queryClient.invalidateQueries({
            queryKey: chatMessagesQueryKey(ownerKey, conversationId),
          }),
        );
      }
      await Promise.all(requests);
    },
    [ownerKey, queryClient],
  );

  const finishRunFromResult = useCallback(
    (
      response: AiChatCompletionResponse,
      cursor: number,
      terminal: AiChatStreamEvent | null,
      fence: ChatRunFence,
    ): boolean => {
      if (response.status === 'processing') {
        return chatRunFenceMatches(stateRef.current.activeRun, fence) ? false : true;
      }
      const succeeded = response.status === 'succeeded' && Boolean(response.text?.trim());
      const terminalError = terminal?.type === 'error' ? terminal.error : null;
      let applied = false;
      let conversationId: string | null = null;
      updateState((current) => {
        if (!chatRunFenceMatches(current.activeRun, fence)) return current;
        applied = true;
        const activeRun = current.activeRun!;
        conversationId = response.conversation_id ?? activeRun.conversationId;
        const currentRunTiming = currentRunTimingRef.current;
        if (
          succeeded &&
          currentRunTiming?.clientMessageId === fence.clientMessageId &&
          currentRunTiming.completedAt === null
        ) {
          currentRunTiming.completedAt = Date.now();
        }
        const becameTerminal =
          activeRun.status === 'sending' ||
          activeRun.status === 'processing' ||
          activeRun.recoveryRequired;
        const shouldMarkUnread =
          becameTerminal && (!openRef.current || current.selectedConversationId !== conversationId);
        return {
          ...current,
          activeRun: {
            ...activeRun,
            response,
            conversationId,
            status: succeeded ? 'succeeded' : 'failed',
            cursor,
            streamedText: succeeded ? response.text!.trim() : activeRun.streamedText,
            errorCode: succeeded
              ? null
              : (terminalError?.code ?? response.error?.code ?? 'chat.empty_response'),
            errorMessage: succeeded
              ? null
              : (terminalError?.message ?? response.error?.message ?? null),
            errorDetails: null,
            recoveryRequired: false,
          },
          unreadCount: shouldMarkUnread ? Math.min(999, current.unreadCount + 1) : 0,
          lastOutcome: succeeded ? 'success' : 'error',
        };
      });
      if (!applied) return true;
      void invalidateConversation(conversationId);
      return true;
    },
    [invalidateConversation, updateState],
  );

  const followChatGeneration = useCallback(
    async (options: { checkResultFirst: boolean }, expectedFence?: ChatRunFence): Promise<void> => {
      const initial = stateRef.current.activeRun;
      const fence = expectedFence ?? (initial ? createChatRunFence(initial) : null);
      if (
        !initial?.generationId ||
        !fence?.generationId ||
        !chatRunFenceMatches(initial, fence) ||
        followingRef.current
      ) {
        return;
      }
      followingRef.current = true;
      const controller = new AbortController();
      streamControllerRef.current?.abort();
      streamControllerRef.current = controller;
      const deadline = initial.resumeDeadlineAt ?? Date.now() + CHAT_GENERATION_MAX_FOLLOW_MS;
      if (
        initial.resumeDeadlineAt === null &&
        !updateActiveRunForFence(fence, { resumeDeadlineAt: deadline })
      ) {
        controller.abort('chat.run_replaced');
        followingRef.current = false;
        streamControllerRef.current = null;
        return;
      }
      let cursor = initial.cursor;
      let streamedText = initial.streamedText;
      let terminal: AiChatStreamEvent | null = null;
      let retryAttempt = 0;
      let cursorConflictCount = 0;
      let checkResult = options.checkResultFirst;
      let allowExpiredResultRead = options.checkResultFirst && Date.now() >= deadline;
      let deadlineTimer: number | null = null;

      const stopWithError = (error: unknown, fallbackCode?: string) => {
        const info = apiErrorInfo(error);
        let applied = false;
        updateState((current) => {
          if (!chatRunFenceMatches(current.activeRun, fence)) return current;
          applied = true;
          const activeRun = current.activeRun!;
          const shouldMarkUnread =
            !openRef.current || current.selectedConversationId !== activeRun.conversationId;
          return {
            ...current,
            activeRun: {
              ...activeRun,
              status: 'failed',
              response: null,
              errorCode: fallbackCode ?? info.errorCode,
              errorMessage: info.errorMessage,
              errorDetails: info.errorDetails,
              recoveryRequired: true,
            },
            unreadCount: shouldMarkUnread ? Math.min(999, current.unreadCount + 1) : 0,
            lastOutcome: 'error',
          };
        });
        return applied;
      };
      const stopForFollowDeadline = () => {
        if (controller.signal.aborted) return;
        stopWithError(new Error('Chat generation follow deadline exceeded'), 'chat.follow_timeout');
        controller.abort('chat.follow_timeout');
      };
      const armFollowDeadline = (): boolean => {
        if (Date.now() >= deadline) {
          stopForFollowDeadline();
          return false;
        }
        if (deadlineTimer !== null) return true;
        deadlineTimer = window.setTimeout(stopForFollowDeadline, deadline - Date.now());
        return true;
      };

      if (allowExpiredResultRead) {
        deadlineTimer = window.setTimeout(stopForFollowDeadline, CHAT_EXPIRED_RESULT_READ_GRACE_MS);
      } else if (!armFollowDeadline()) {
        followingRef.current = false;
        streamControllerRef.current = null;
        return;
      }

      const recoverFromError = async (error: unknown): Promise<boolean> => {
        if (controller.signal.aborted) return false;
        const disposition = chatStreamErrorDisposition(error);
        if (disposition === 'cursor_conflict') {
          cursorConflictCount += 1;
          if (cursorConflictCount > 1) {
            if (!stopWithError(error)) controller.abort('chat.run_replaced');
            return false;
          }
          cursor = 0;
          streamedText = '';
          terminal = null;
          retryAttempt = 0;
          checkResult = true;
          if (
            !updateActiveRunForFence(fence, {
              cursor: 0,
              streamedText: '',
              errorCode: null,
              errorMessage: null,
              errorDetails: null,
            })
          ) {
            controller.abort('chat.run_replaced');
            return false;
          }
          return true;
        }
        if (disposition === 'permanent') {
          if (!stopWithError(error)) controller.abort('chat.run_replaced');
          return false;
        }
        retryAttempt += 1;
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          stopForFollowDeadline();
          return false;
        }
        if (retryAttempt > CHAT_STREAM_MAX_RETRIES) {
          if (!stopWithError(error, 'chat.stream_retry_exhausted')) {
            controller.abort('chat.run_replaced');
          }
          return false;
        }
        checkResult = true;
        await retryDelay(
          controller.signal,
          Math.min(remaining, chatStreamRetryDelayMs(retryAttempt)),
        );
        if (controller.signal.aborted) return false;
        if (!chatRunFenceMatches(stateRef.current.activeRun, fence)) {
          controller.abort('chat.run_replaced');
          return false;
        }
        return true;
      };

      try {
        while (!controller.signal.aborted && terminal === null) {
          if (!chatRunFenceMatches(stateRef.current.activeRun, fence)) {
            controller.abort('chat.run_replaced');
            break;
          }
          const generationId = fence.generationId;
          if (checkResult) {
            try {
              const response = await apiClient.chatGeneration(generationId, {
                signal: controller.signal,
              });
              if (finishRunFromResult(response, cursor, terminal, fence)) return;
              checkResult = false;
              if (allowExpiredResultRead) {
                allowExpiredResultRead = false;
                if (deadlineTimer !== null) window.clearTimeout(deadlineTimer);
                deadlineTimer = null;
              }
            } catch (error) {
              if (!(await recoverFromError(error))) return;
              continue;
            }
          }
          if (!armFollowDeadline()) return;

          let connectionEvents = 0;
          try {
            for await (const event of apiClient.streamChatGeneration(generationId, {
              cursor,
              signal: controller.signal,
            })) {
              if (event.cursor <= cursor) continue;
              connectionEvents += 1;
              retryAttempt = 0;
              cursor = event.cursor;
              const update =
                event.type === 'delta'
                  ? (() => {
                      streamedText += event.text;
                      return { cursor, streamedText, status: 'processing' as const };
                    })()
                  : { cursor };
              if (!updateActiveRunForFence(fence, update)) {
                controller.abort('chat.run_replaced');
                break;
              }
              if (event.type === 'final' || event.type === 'error') {
                terminal = event;
                break;
              }
            }
          } catch (error) {
            if (!(await recoverFromError(error))) return;
            continue;
          }
          if (terminal !== null) {
            checkResult = true;
            try {
              const response = await apiClient.chatGeneration(generationId, {
                signal: controller.signal,
              });
              if (finishRunFromResult(response, cursor, terminal, fence)) return;
            } catch (error) {
              if (!(await recoverFromError(error))) return;
            }
            terminal = null;
            checkResult = true;
            continue;
          }
          if (connectionEvents > 0) continue;
          if (!(await recoverFromError(new ApiError('chat.stream_ended', 503)))) return;
        }
      } finally {
        if (deadlineTimer !== null) window.clearTimeout(deadlineTimer);
        if (streamControllerRef.current === controller) {
          followingRef.current = false;
          streamControllerRef.current = null;
        }
      }
    },
    [finishRunFromResult, updateActiveRunForFence, updateState],
  );

  const sendActiveRun = useCallback(
    async (expectedFence?: ChatRunFence) => {
      if (admissionRef.current) return;
      const run = stateRef.current.activeRun;
      if (!run) return;
      const fence = expectedFence ?? createChatRunFence(run);
      if (!chatRunFenceMatches(run, fence)) return;
      admissionRef.current = true;
      try {
        if (
          !updateActiveRunForFence(fence, {
            status: run.generationId ? 'processing' : 'sending',
            errorCode: null,
            errorMessage: null,
            errorDetails: null,
            recoveryRequired: false,
          })
        ) {
          return;
        }
        const accepted = run.accepted ?? (await apiClient.chatCompletion(run.request));
        const sourceKey = chatComposerKey(run.request.conversation_id ?? null);
        let applied = false;
        updateState((current) => {
          if (!chatRunFenceMatches(current.activeRun, fence)) return current;
          applied = true;
          const sourceComposer =
            current.composers[sourceKey] ?? createEmptyComposer(run.request.model_id ?? null);
          const nextComposer = {
            ...sourceComposer,
            draft: '',
            attachments: [],
            errorCode: null,
            modelId: run.request.model_id ?? sourceComposer.modelId,
          };
          const acceptedKey = chatComposerKey(accepted.conversation_id);
          return {
            ...current,
            selectedConversationId:
              current.selectedConversationId === (run.request.conversation_id ?? null)
                ? accepted.conversation_id
                : current.selectedConversationId,
            recentModelId: accepted.model_id,
            composers: {
              ...current.composers,
              [sourceKey]:
                sourceKey === acceptedKey ? nextComposer : createEmptyComposer(accepted.model_id),
              [acceptedKey]: nextComposer,
            },
            activeRun: current.activeRun
              ? {
                  ...current.activeRun,
                  accepted,
                  generationId: accepted.generation_id,
                  conversationId: accepted.conversation_id,
                  userMessageId: accepted.user_message_id,
                  assistantMessageId: accepted.assistant_message_id,
                  cursor: Math.max(current.activeRun.cursor, accepted.cursor),
                  status: 'processing',
                  resumeDeadlineAt:
                    current.activeRun.resumeDeadlineAt ??
                    Date.now() + CHAT_GENERATION_MAX_FOLLOW_MS,
                  recoveryRequired: false,
                }
              : null,
          };
        });
        if (!applied) return;
        void invalidateConversation(accepted.conversation_id);
        await followChatGeneration(
          { checkResultFirst: run.accepted !== null },
          { clientMessageId: fence.clientMessageId, generationId: accepted.generation_id },
        );
      } catch (error) {
        const info = apiErrorInfo(error);
        const deterministic = isDeterministicAdmissionError(error);
        updateState((current) => {
          if (!chatRunFenceMatches(current.activeRun, fence)) return current;
          const sourceKey = chatComposerKey(run.request.conversation_id ?? null);
          const sourceComposer =
            current.composers[sourceKey] ?? createEmptyComposer(run.request.model_id ?? null);
          return {
            ...current,
            composers: deterministic
              ? {
                  ...current.composers,
                  [sourceKey]: { ...sourceComposer, errorCode: info.errorCode },
                }
              : current.composers,
            activeRun: current.activeRun
              ? {
                  ...current.activeRun,
                  status: 'failed',
                  response: null,
                  ...info,
                  recoveryRequired: !deterministic,
                }
              : null,
            lastOutcome: 'error',
          };
        });
      } finally {
        admissionRef.current = false;
      }
    },
    [followChatGeneration, invalidateConversation, updateActiveRunForFence, updateState],
  );

  useEffect(() => {
    if (recoveryStartedRef.current) return;
    recoveryStartedRef.current = true;
    const run = stateRef.current.activeRun;
    if (!run) return;
    const fence = createChatRunFence(run);
    if (!run.generationId) {
      if (run.recoveryRequired || run.status === 'sending') void sendActiveRun(fence);
      return;
    }
    if (run.status === 'processing' || run.recoveryRequired) {
      void followChatGeneration({ checkResultFirst: true });
      return;
    }
    const controller = new AbortController();
    streamControllerRef.current = controller;
    void apiClient
      .chatGeneration(run.generationId, { signal: controller.signal })
      .then((response) => finishRunFromResult(response, run.cursor, null, fence))
      .catch(() => undefined)
      .finally(() => {
        if (streamControllerRef.current === controller) streamControllerRef.current = null;
      });
  }, [finishRunFromResult, followChatGeneration, sendActiveRun]);

  useEffect(
    () => () => {
      conversationSelectionTokenRef.current += 1;
      streamControllerRef.current?.abort();
      streamControllerRef.current = null;
      followingRef.current = false;
      recoveryStartedRef.current = false;
    },
    [],
  );

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setOpenState(nextOpen);
      if (nextOpen) {
        updateState((current) => ({ ...current, unreadCount: 0 }));
        setFocusRequest((current) => current + 1);
      } else {
        setHistoryOpen(false);
      }
    },
    [updateState],
  );

  const toggleOpen = useCallback(() => setOpen(!openRef.current), [setOpen]);

  const updateComposer = useCallback(
    (update: (composer: ChatComposerState) => ChatComposerState) => {
      const key = chatComposerKey(stateRef.current.selectedConversationId);
      updateState((current) => {
        const currentComposer =
          current.composers[key] ?? createEmptyComposer(current.recentModelId);
        return {
          ...current,
          composers: { ...current.composers, [key]: update(currentComposer) },
        };
      });
    },
    [updateState],
  );

  const updateComposerByKey = useCallback(
    (key: string, update: (composer: ChatComposerState) => ChatComposerState) => {
      updateState((current) => {
        const currentComposer =
          current.composers[key] ?? createEmptyComposer(current.recentModelId);
        return {
          ...current,
          composers: { ...current.composers, [key]: update(currentComposer) },
        };
      });
    },
    [updateState],
  );

  const setDraft = useCallback(
    (draft: string) => updateComposer((current) => ({ ...current, draft, errorCode: null })),
    [updateComposer],
  );
  const setModelId = useCallback(
    (modelId: string) => {
      updateState((current) => {
        const key = chatComposerKey(current.selectedConversationId);
        const currentComposer = current.composers[key] ?? createEmptyComposer(modelId);
        return {
          ...current,
          recentModelId: modelId,
          composers: {
            ...current.composers,
            [key]: { ...currentComposer, modelId, errorCode: null },
          },
        };
      });
    },
    [updateState],
  );

  const uploadAttachment = useCallback(
    async (
      composerTargetKey: string,
      localId: string,
      file: File,
      existingDescriptorId?: string,
    ) => {
      try {
        let attachmentId = existingDescriptorId;
        if (!attachmentId) {
          const created = await apiClient.createChatAttachment(
            await chatAttachmentCreateRequest(file),
          );
          attachmentId = created.attachment.id;
          const {
            status: _createdStatus,
            expires_at: _createdExpiresAt,
            ...createdDescriptor
          } = created.attachment;
          updateComposerByKey(composerTargetKey, (current) => ({
            ...current,
            attachments: current.attachments.map((attachment) =>
              attachment.localId === localId
                ? { ...attachment, descriptor: createdDescriptor, errorCode: null }
                : attachment,
            ),
          }));
        }
        const uploaded = await apiClient.uploadChatAttachment(attachmentId, file, file.type);
        const {
          status: _uploadedStatus,
          expires_at: _uploadedExpiresAt,
          ...uploadedDescriptor
        } = uploaded.attachment;
        updateComposerByKey(composerTargetKey, (current) => ({
          ...current,
          attachments: current.attachments.map((attachment) =>
            attachment.localId === localId
              ? {
                  ...attachment,
                  status: 'uploaded',
                  descriptor: uploadedDescriptor,
                  errorCode: null,
                }
              : attachment,
          ),
        }));
      } catch (error) {
        const code =
          error instanceof ChatFileMetadataError
            ? error.code
            : error instanceof ApiError
              ? error.code
              : 'chat.attachment_upload_failed';
        updateComposerByKey(composerTargetKey, (current) => ({
          ...current,
          attachments: current.attachments.map((attachment) =>
            attachment.localId === localId
              ? { ...attachment, status: 'error', errorCode: code }
              : attachment,
          ),
        }));
      }
    },
    [updateComposerByKey],
  );

  const addAttachments = useCallback(
    async (files: readonly File[]) => {
      const targetKey = chatComposerKey(stateRef.current.selectedConversationId);
      const targetComposer =
        stateRef.current.composers[targetKey] ??
        createEmptyComposer(stateRef.current.recentModelId);
      if (!selectedModel) {
        updateComposerByKey(targetKey, (current) => ({
          ...current,
          errorCode: 'chat.model_unavailable',
        }));
        return;
      }
      const available = Math.max(0, attachmentMaxCount - targetComposer.attachments.length);
      const selected = [...files].slice(0, available);
      if (selected.length === 0) {
        updateComposerByKey(targetKey, (current) => ({
          ...current,
          errorCode: 'chat.attachment_count_exceeded',
        }));
        return;
      }
      const items: ChatAttachmentItem[] = selected.map((file) => ({
        localId: createAttachmentLocalId(),
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        status: 'uploading',
        descriptor: null,
        errorCode: null,
        file,
      }));
      updateComposerByKey(targetKey, (current) => ({
        ...current,
        attachments: [...current.attachments, ...items],
        errorCode: files.length > selected.length ? 'chat.attachment_count_exceeded' : null,
      }));
      await Promise.all(items.map((item) => uploadAttachment(targetKey, item.localId, item.file!)));
    },
    [attachmentMaxCount, selectedModel, updateComposerByKey, uploadAttachment],
  );

  const retryAttachment = useCallback(
    async (localId: string) => {
      const targetKey = chatComposerKey(stateRef.current.selectedConversationId);
      const item = stateRef.current.composers[targetKey]?.attachments.find(
        (attachment) => attachment.localId === localId,
      );
      if (!item?.file) {
        updateComposerByKey(targetKey, (current) => ({
          ...current,
          attachments: current.attachments.map((attachment) =>
            attachment.localId === localId
              ? { ...attachment, errorCode: 'chat.attachment_reselect_required' }
              : attachment,
          ),
        }));
        return;
      }
      updateComposerByKey(targetKey, (current) => ({
        ...current,
        attachments: current.attachments.map((attachment) =>
          attachment.localId === localId
            ? { ...attachment, status: 'uploading', errorCode: null }
            : attachment,
        ),
      }));
      await uploadAttachment(targetKey, item.localId, item.file, item.descriptor?.id);
    },
    [updateComposerByKey, uploadAttachment],
  );

  const removeAttachment = useCallback(
    async (localId: string) => {
      const targetKey = chatComposerKey(stateRef.current.selectedConversationId);
      const item = stateRef.current.composers[targetKey]?.attachments.find(
        (attachment) => attachment.localId === localId,
      );
      if (!item || item.status === 'uploading') return;
      setAttachmentActionIds((current) => new Set(current).add(localId));
      try {
        if (item.descriptor) await apiClient.deleteChatAttachment(item.descriptor.id);
        updateComposerByKey(targetKey, (current) => ({
          ...current,
          attachments: current.attachments.filter((attachment) => attachment.localId !== localId),
          errorCode: null,
        }));
      } catch (error) {
        const code = error instanceof ApiError ? error.code : 'chat.attachment_delete_failed';
        updateComposerByKey(targetKey, (current) => ({
          ...current,
          attachments: current.attachments.map((attachment) =>
            attachment.localId === localId
              ? { ...attachment, status: 'error', errorCode: code }
              : attachment,
          ),
        }));
      } finally {
        setAttachmentActionIds((current) => {
          const next = new Set(current);
          next.delete(localId);
          return next;
        });
      }
    },
    [updateComposerByKey],
  );

  const send = useCallback(() => {
    if (!canSend || !selectedModel || admissionRef.current) return;
    admissionRef.current = true;
    setAdmissionLoading(true);
    const current = stateRef.current;
    const key = chatComposerKey(current.selectedConversationId);
    const currentComposer = current.composers[key] ?? createEmptyComposer(selectedModel.id);
    const draft = currentComposer.draft.trim();
    const clientMessageId = createClientMessageId();
    currentRunTimingRef.current = {
      clientMessageId,
      startedAt: Date.now(),
      completedAt: null,
    };
    const descriptors = currentComposer.attachments.flatMap((attachment) =>
      attachment.status === 'uploaded' && attachment.descriptor ? [attachment.descriptor] : [],
    );
    void (async () => {
      let readyToAdmit = false;
      try {
        const submissionHistory = current.selectedConversationId
          ? await loadChatSubmissionHistory(current.selectedConversationId)
          : [];
        const request: AiChatCompletionRequest = {
          client_message_id: clientMessageId,
          ...(current.selectedConversationId
            ? { conversation_id: current.selectedConversationId }
            : {}),
          messages: buildChatRequestMessages(
            submissionHistory,
            draft,
            descriptors.map((attachment) => attachment.id),
          ),
          model_id: selectedModel.id,
          max_output_tokens: CHAT_MAX_OUTPUT_TOKENS,
        };
        const run: ChatRun = {
          request,
          attachments: descriptors,
          accepted: null,
          response: null,
          generationId: null,
          conversationId: current.selectedConversationId,
          userMessageId: null,
          assistantMessageId: null,
          status: 'sending',
          cursor: 0,
          streamedText: '',
          errorCode: null,
          errorMessage: null,
          errorDetails: null,
          resumeDeadlineAt: null,
          recoveryRequired: false,
        };
        updateState((currentState) => ({
          ...currentState,
          activeRun: run,
          lastOutcome: 'idle',
          composers: {
            ...currentState.composers,
            [key]: { ...currentComposer, errorCode: null },
          },
        }));
        readyToAdmit = true;
      } catch (error) {
        if (currentRunTimingRef.current?.clientMessageId === clientMessageId) {
          currentRunTimingRef.current = null;
        }
        const info = apiErrorInfo(error);
        updateComposerByKey(key, (composer) => ({ ...composer, errorCode: info.errorCode }));
      } finally {
        setAdmissionLoading(false);
        admissionRef.current = false;
      }
      if (readyToAdmit) {
        void sendActiveRun({ clientMessageId, generationId: null });
      }
    })();
  }, [canSend, selectedModel, sendActiveRun, updateComposerByKey, updateState]);

  const resumeActiveRun = useCallback(() => {
    const run = stateRef.current.activeRun;
    if (!run) return;
    const fence = createChatRunFence(run);
    let resumed = false;
    updateState((current) => {
      if (!chatRunFenceMatches(current.activeRun, fence)) return current;
      resumed = true;
      return {
        ...current,
        lastOutcome: 'idle',
        activeRun: {
          ...current.activeRun!,
          status: run.generationId ? 'processing' : 'sending',
          recoveryRequired: false,
        },
      };
    });
    if (!resumed) return;
    if (run.generationId) void followChatGeneration({ checkResultFirst: true }, fence);
    else void sendActiveRun(fence);
  }, [followChatGeneration, sendActiveRun, updateState]);

  const prepareRetry = useCallback(
    (message: ChatDisplayMessage, previousUser: ChatDisplayMessage | null) => {
      const source = message.role === 'user' ? message : previousUser;
      if (!source?.content) return;
      const retryContent = source.content;
      updateState((current) => {
        const conversationId = source.conversationId ?? current.selectedConversationId;
        const key = chatComposerKey(conversationId);
        const currentComposer =
          current.composers[key] ?? createEmptyComposer(current.recentModelId);
        const requestedModelId = message.requestedModelId ?? message.modelId;
        return {
          ...current,
          selectedConversationId: conversationId,
          recentModelId: requestedModelId ?? current.recentModelId,
          composers: {
            ...current.composers,
            [key]: {
              ...currentComposer,
              draft: retryContent,
              modelId: requestedModelId ?? currentComposer.modelId,
              errorCode: source.attachments.length > 0 ? 'chat.retry_attachments_required' : null,
            },
          },
        };
      });
      setOpen(true);
      setFocusRequest((current) => current + 1);
    },
    [setOpen, updateState],
  );

  const selectConversation = useCallback(
    async (conversationId: string) => {
      const token = conversationSelectionTokenRef.current + 1;
      conversationSelectionTokenRef.current = token;
      setConversationSelectionError(false);

      if (stateRef.current.selectedConversationId === conversationId) {
        setFocusRequest((current) => current + 1);
        return true;
      }

      try {
        await queryClient.fetchInfiniteQuery({
          queryKey: chatMessagesQueryKey(ownerKey, conversationId),
          queryFn: ({ pageParam, signal }) =>
            loadChatMessagesPage(conversationId, pageParam, signal),
          initialPageParam: null as string | null,
          getNextPageParam: (page) => page.next_cursor,
          pages: 1,
          retry: false,
        });
      } catch {
        if (conversationSelectionTokenRef.current === token) {
          setConversationSelectionError(true);
        }
        return false;
      }

      if (conversationSelectionTokenRef.current !== token) return false;
      let committed = false;
      updateState((current) => {
        if (conversationSelectionTokenRef.current !== token) return current;
        committed = true;
        return {
          ...current,
          selectedConversationId: conversationId,
          composers: current.composers[conversationId]
            ? current.composers
            : {
                ...current.composers,
                [conversationId]: createEmptyComposer(null),
              },
        };
      });
      if (!committed) return false;
      setConversationSelectionError(false);
      setFocusRequest((current) => current + 1);
      return true;
    },
    [ownerKey, queryClient, updateState],
  );

  const startNewConversation = useCallback(() => {
    conversationSelectionTokenRef.current += 1;
    setConversationSelectionError(false);
    updateState((current) => {
      const key = chatComposerKey(null);
      return {
        ...current,
        selectedConversationId: null,
        composers: current.composers[key]
          ? current.composers
          : { ...current.composers, [key]: createEmptyComposer(current.recentModelId) },
      };
    });
    setFocusRequest((current) => current + 1);
  }, [updateState]);

  const renameConversation = useCallback(
    async (conversationId: string, title: string) => {
      setHistoryMutationId(conversationId);
      try {
        await apiClient.renameChatConversation(conversationId, { title });
        await invalidateConversation(conversationId);
        return true;
      } catch {
        return false;
      } finally {
        setHistoryMutationId(null);
      }
    },
    [invalidateConversation],
  );

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      const active = stateRef.current.activeRun;
      if (
        active?.conversationId === conversationId &&
        (active.status === 'sending' || active.status === 'processing')
      ) {
        return false;
      }
      setHistoryMutationId(conversationId);
      try {
        await apiClient.deleteChatConversation(conversationId);
        updateState((current) => {
          const composers = { ...current.composers };
          delete composers[conversationId];
          return {
            ...current,
            selectedConversationId:
              current.selectedConversationId === conversationId
                ? null
                : current.selectedConversationId,
            composers,
          };
        });
        await invalidateConversation(conversationId);
        return true;
      } catch {
        return false;
      } finally {
        setHistoryMutationId(null);
      }
    },
    [invalidateConversation, updateState],
  );

  const displayMessages = useMemo(
    () =>
      buildDisplayMessages(
        historyMessages,
        state.activeRun,
        state.selectedConversationId,
        currentRunTimingRef.current,
      ),
    [historyMessages, state.activeRun, state.selectedConversationId],
  );

  const reset = useCallback(() => {
    conversationSelectionTokenRef.current += 1;
    streamControllerRef.current?.abort();
    streamControllerRef.current = null;
    followingRef.current = false;
    admissionRef.current = false;
    currentRunTimingRef.current = null;
    setAdmissionLoading(false);
    setConversationSelectionError(false);
    const empty = createEmptyChatState();
    stateRef.current = empty;
    setState(empty);
    setOpenState(false);
    setHistoryOpen(false);
    clearPersistedChatState(ownerKey);
  }, [ownerKey]);

  const contextValue = useMemo<ChatContextValue>(
    () => ({
      open,
      historyOpen,
      selectedConversationId: state.selectedConversationId,
      selectedConversation,
      conversations,
      conversationsLoading: conversationsQuery.isLoading,
      conversationsError: conversationsQuery.error,
      conversationsHasMore: conversationsQuery.hasNextPage,
      conversationsFetchingMore: conversationsQuery.isFetchingNextPage,
      messages: displayMessages,
      historyMessages,
      messagesLoading: messagesQuery.isLoading,
      messagesError: messagesQuery.error,
      messagesHasMore: messagesQuery.hasNextPage,
      messagesFetchingMore: messagesQuery.isFetchingNextPage,
      composer,
      modelFeature,
      modelOptions: modelFeature?.models ?? [],
      selectedModel,
      modelsLoading: modelsQuery.isLoading,
      modelsError: modelsQuery.error,
      attachmentIssue: attachmentValidation,
      attachmentMaxCount,
      attachmentActionIds,
      canSend,
      busy,
      activeRun: state.activeRun,
      unreadCount: state.unreadCount,
      lastOutcome: state.lastOutcome,
      focusRequest,
      historyMutationId,
      conversationSelectionError,
      setOpen,
      toggleOpen,
      setHistoryOpen,
      selectConversation,
      startNewConversation,
      setDraft,
      setModelId,
      addAttachments,
      retryAttachment,
      removeAttachment,
      send,
      resumeActiveRun,
      prepareRetry,
      loadMoreConversations: () => void conversationsQuery.fetchNextPage(),
      retryConversations: () => void conversationsQuery.refetch(),
      loadMoreMessages: () => void messagesQuery.fetchNextPage(),
      retryMessages: () => void messagesQuery.refetch(),
      retryModels: () => void modelsQuery.refetch(),
      renameConversation,
      deleteConversation,
      reset,
    }),
    [
      addAttachments,
      attachmentActionIds,
      attachmentMaxCount,
      attachmentValidation,
      busy,
      canSend,
      composer,
      conversations,
      conversationsQuery,
      deleteConversation,
      displayMessages,
      focusRequest,
      historyMessages,
      historyMutationId,
      conversationSelectionError,
      historyOpen,
      messagesQuery,
      modelFeature,
      modelsQuery,
      open,
      prepareRetry,
      removeAttachment,
      renameConversation,
      reset,
      resumeActiveRun,
      retryAttachment,
      selectConversation,
      selectedConversation,
      selectedModel,
      send,
      setDraft,
      setModelId,
      setOpen,
      startNewConversation,
      state.activeRun,
      state.lastOutcome,
      state.selectedConversationId,
      state.unreadCount,
      toggleOpen,
    ],
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used inside ChatProvider');
  return context;
}

function currentChatOwnerKey(): string {
  const identity = readStoredSessionIdentity();
  if (identity) return `${identity.tenantId}:${identity.userId}`;
  const user = readStoredUser();
  return user ? (user.id ?? user.email) : 'anonymous';
}

function chatMessagesQueryKey(ownerKey: string, conversationId: string | null) {
  return ['chat', ownerKey, 'messages', conversationId] as const;
}

async function loadChatMessagesPage(
  conversationId: string,
  pageParam: string | null,
  signal: AbortSignal,
) {
  signal.throwIfAborted();
  const page = await apiClient.listChatMessages(conversationId, {
    ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
    limit: CHAT_MESSAGES_PAGE_LIMIT,
  });
  signal.throwIfAborted();
  return page;
}

async function loadChatSubmissionHistory(conversationId: string): Promise<AiChatStoredMessage[]> {
  const messages: AiChatStoredMessage[] = [];
  const messageIds = new Set<string>();
  const cursors = new Set<string>();
  let cursor: string | null = null;
  do {
    const page = await apiClient.listChatMessages(conversationId, {
      ...(cursor ? { cursor } : {}),
      limit: 100,
    });
    for (const message of page.items) {
      if (messageIds.has(message.id)) continue;
      messageIds.add(message.id);
      messages.push(message);
    }
    if (selectChatRequestHistory(messages).length >= CHAT_MESSAGE_MAX_COUNT - 1) break;
    if (!page.next_cursor || cursors.has(page.next_cursor)) break;
    cursors.add(page.next_cursor);
    cursor = page.next_cursor;
  } while (cursor);
  return messages;
}

function buildDisplayMessages(
  history: readonly AiChatStoredMessage[],
  activeRun: ChatRun | null,
  selectedConversationId: string | null,
  currentRunTiming: CurrentRunTiming | null,
): ChatDisplayMessage[] {
  const visibleActiveRun =
    activeRun &&
    (activeRun.conversationId === selectedConversationId ||
      (activeRun.conversationId === null && selectedConversationId === null))
      ? activeRun
      : null;
  const activeAssistantId = visibleActiveRun
    ? (visibleActiveRun.assistantMessageId ??
      `local-assistant-${visibleActiveRun.request.client_message_id}`)
    : null;
  const result = history.map<ChatDisplayMessage>((message) => {
    const stored: ChatDisplayMessage = {
      id: message.id,
      conversationId: message.conversation_id,
      role: message.role,
      content: message.content,
      attachments: message.attachments,
      status: message.status,
      requestedModelId: message.requested_model_id,
      modelId: message.model_id,
      usage: message.usage,
      error: message.error,
      response: null,
      ...displayMessageTiming(message.created_at, message.completed_at),
      local: false,
    };
    return visibleActiveRun && message.role === 'assistant' && message.id === activeAssistantId
      ? mergeActiveRunAssistant(stored, visibleActiveRun, undefined, currentRunTiming)
      : stored;
  });
  if (!visibleActiveRun) return result;
  const currentRun = visibleActiveRun;
  const submittedUser = [...currentRun.request.messages]
    .reverse()
    .find((message) => message.role === 'user');
  const userId = currentRun.userMessageId ?? `local-user-${currentRun.request.client_message_id}`;
  const assistantId =
    currentRun.assistantMessageId ?? `local-assistant-${currentRun.request.client_message_id}`;
  if (!result.some((message) => message.id === userId)) {
    result.push({
      id: userId,
      conversationId: currentRun.conversationId,
      role: 'user',
      content: submittedUser?.content ?? null,
      attachments: currentRun.attachments,
      status: 'succeeded',
      requestedModelId: currentRun.request.model_id ?? null,
      modelId: null,
      usage: null,
      error: null,
      response: null,
      startedAt: null,
      completedAt: null,
      local: true,
    });
  }
  if (!result.some((message) => message.id === assistantId)) {
    result.push(mergeActiveRunAssistant(null, currentRun, assistantId, currentRunTiming));
  }
  return result;
}

function mergeActiveRunAssistant(
  stored: ChatDisplayMessage | null,
  activeRun: ChatRun,
  fallbackId?: string,
  currentRunTiming: CurrentRunTiming | null = null,
): ChatDisplayMessage {
  const responseText = activeRun.response?.text;
  const content = responseText?.trim()
    ? responseText
    : activeRun.streamedText.trim()
      ? activeRun.streamedText
      : (stored?.content ?? null);
  const timing =
    currentRunTiming?.clientMessageId === activeRun.request.client_message_id
      ? currentRunTiming
      : null;
  return {
    id:
      stored?.id ??
      fallbackId ??
      activeRun.assistantMessageId ??
      `local-assistant-${activeRun.request.client_message_id}`,
    conversationId: activeRun.conversationId,
    role: 'assistant',
    content,
    attachments: stored?.attachments ?? [],
    status:
      activeRun.status === 'sending' || activeRun.status === 'processing'
        ? 'processing'
        : activeRun.status,
    requestedModelId: activeRun.request.model_id ?? stored?.requestedModelId ?? null,
    modelId:
      activeRun.response?.model_id ?? activeRun.accepted?.model_id ?? stored?.modelId ?? null,
    usage: activeRun.response ? activeRun.response.usage : (stored?.usage ?? null),
    error:
      activeRun.status === 'failed'
        ? activeRun.response?.error
          ? {
              code: activeRun.response.error.code,
              message: activeRun.response.error.message ?? null,
            }
          : {
              code: activeRun.errorCode ?? 'chat.failed',
              message: activeRun.errorMessage,
            }
        : null,
    response: activeRun.response,
    startedAt: timing?.startedAt ?? null,
    completedAt: timing && activeRun.status === 'succeeded' ? timing.completedAt : null,
    local: true,
  };
}

function displayMessageTiming(
  createdAt: string,
  completedAt: string | null,
): Pick<ChatDisplayMessage, 'startedAt' | 'completedAt'> {
  const startedAt = Date.parse(createdAt);
  const finishedAt = completedAt === null ? Number.NaN : Date.parse(completedAt);
  if (!Number.isFinite(startedAt) || !Number.isFinite(finishedAt) || finishedAt < startedAt) {
    return { startedAt: null, completedAt: null };
  }
  return { startedAt, completedAt: finishedAt };
}

function retryDelay(signal: AbortSignal, delayMs: number): Promise<void> {
  if (signal.aborted || delayMs <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, delayMs);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
